/**
 * ScanEngine — Singleton managing OpenCV.js loading and edge detection.
 * OpenCV is loaded ONCE lazily (first call to loadOpenCV).
 * All Mats are cleaned up after each detectEdges() call.
 */

let cvReady = false;
let loadPromise = null;

const OPENCV_CDN = 'https://cdn.jsdelivr.net/npm/@techstark/opencv-js@4.10.0-release.1/dist/opencv.js';

export function isOpenCVReady() {
  return cvReady;
}

export function loadOpenCV() {
  if (cvReady) return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    // Module callback for WASM init
    if (typeof window.Module === 'undefined') window.Module = {};
    const prev = window.Module.onRuntimeInitialized;
    window.Module.onRuntimeInitialized = function () {
      if (prev) prev();
      cvReady = true;
      resolve();
    };

    const script = document.createElement('script');
    script.src = OPENCV_CDN;
    script.async = true;
    script.onerror = () => reject(new Error('OpenCV load failed'));
    document.head.appendChild(script);

    // Polling fallback
    const poll = setInterval(() => {
      try {
        if (typeof cv !== 'undefined' && typeof cv.Mat === 'function') {
          clearInterval(poll);
          if (!cvReady) { cvReady = true; resolve(); }
        }
      } catch (e) { /* not ready */ }
    }, 500);

    setTimeout(() => { clearInterval(poll); if (!cvReady) reject(new Error('OpenCV timeout')); }, 30000);
  });

  // Don't let rejection break future calls
  loadPromise.catch(() => { loadPromise = null; });

  return loadPromise;
}

/**
 * Detect document edges in an image. Returns 4 ordered corners [TL, TR, BR, BL]
 * in original image coordinates, or null if no document found.
 */
export function detectEdges(imageElement) {
  if (!cvReady) return null;

  const imgW = imageElement.naturalWidth || imageElement.width;
  const imgH = imageElement.naturalHeight || imageElement.height;

  const tmpCanvas = document.createElement('canvas');
  const maxDim = 800;
  const scale = Math.min(1, maxDim / Math.max(imgW, imgH));
  tmpCanvas.width = Math.round(imgW * scale);
  tmpCanvas.height = Math.round(imgH * scale);
  tmpCanvas.getContext('2d').drawImage(imageElement, 0, 0, tmpCanvas.width, tmpCanvas.height);

  const mats = []; // track all Mats for cleanup
  const mat = (m) => { mats.push(m); return m; };

  try {
    const src = mat(cv.imread(tmpCanvas));
    const gray = mat(new cv.Mat());
    const equalized = mat(new cv.Mat());
    const blurred = mat(new cv.Mat());

    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
    // Histogram equalization for better contrast in variable lighting
    cv.equalizeHist(gray, equalized);
    cv.GaussianBlur(equalized, blurred, new cv.Size(5, 5), 0);

    const imgArea = src.rows * src.cols;
    let bestArea = imgArea * 0.03; // Lower threshold: 3% instead of 5%
    let bestPoints = null;

    // Try multiple Canny thresholds (aggressive to conservative)
    const thresholds = [[30, 120], [50, 150], [75, 200]];
    for (const [low, high] of thresholds) {
      const edges = mat(new cv.Mat());
      const dilated = mat(new cv.Mat());
      cv.Canny(blurred, edges, low, high);

      // Stronger dilation to connect broken edges
      const kernel = mat(cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(5, 5)));
      cv.dilate(edges, dilated, kernel);

      const contours = mat(new cv.MatVector());
      const hierarchy = mat(new cv.Mat());
      cv.findContours(dilated, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);

      for (let i = 0; i < contours.size(); i++) {
        const contour = contours.get(i);
        const area = cv.contourArea(contour);
        if (area < bestArea) continue;

        const peri = cv.arcLength(contour, true);
        const approx = mat(new cv.Mat());
        cv.approxPolyDP(contour, approx, 0.03 * peri, true); // Slightly more tolerant

        if (approx.rows === 4 && cv.isContourConvex(approx) && area > bestArea) {
          bestArea = area;
          const pts = [];
          for (let j = 0; j < 4; j++) {
            pts.push({ x: approx.data32S[j * 2] / scale, y: approx.data32S[j * 2 + 1] / scale });
          }
          bestPoints = pts;
        }
      }

      // If we found a good result with aggressive thresholds, stop
      if (bestPoints && bestArea > imgArea * 0.1) break;
    }

    return bestPoints ? orderCorners(bestPoints) : null;
  } catch (e) {
    console.error('Edge detection error:', e);
    return null;
  } finally {
    mats.forEach((m) => { try { m.delete(); } catch (e) { /* already freed */ } });
  }
}

function orderCorners(pts) {
  let tl = pts[0], tr = pts[0], br = pts[0], bl = pts[0];
  let minSum = Infinity, maxSum = -Infinity, minDiff = Infinity, maxDiff = -Infinity;

  for (const p of pts) {
    const s = p.x + p.y, d = p.x - p.y;
    if (s < minSum) { minSum = s; tl = p; }
    if (s > maxSum) { maxSum = s; br = p; }
    if (d > maxDiff) { maxDiff = d; tr = p; }
    if (d < minDiff) { minDiff = d; bl = p; }
  }

  return [tl, tr, br, bl];
}
